import {
  useState,
  createContext,
  useContext,
  type ReactNode,
  type HTMLAttributes,
} from 'react'
import { cn } from '@lib/utils'

interface TabsContextValue {
  activeTab: string
  setActiveTab: (tab: string) => void
}

const TabsContext = createContext<TabsContextValue | null>(null)

function useTabsContext() {
  const context = useContext(TabsContext)
  if (!context) {
    throw new Error('Tab components must be used within a Tabs component')
  }
  return context
}

export interface TabsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Currently active tab value */
  value?: string
  /** Default active tab */
  defaultValue?: string
  /** Change handler */
  onChange?: (value: string) => void
  children: ReactNode
}

/**
 * Tabs container component
 *
 * @example
 * <Tabs defaultValue="overview">
 *   <TabList>
 *     <Tab value="overview">Overview</Tab>
 *     <Tab value="keywords">Keywords</Tab>
 *   </TabList>
 *   <TabPanels>
 *     <TabPanel value="overview">Overview content</TabPanel>
 *     <TabPanel value="keywords">Keywords content</TabPanel>
 *   </TabPanels>
 * </Tabs>
 */
export function Tabs({
  value,
  defaultValue,
  onChange,
  children,
  className,
  ...props
}: TabsProps) {
  const [internalValue, setInternalValue] = useState(defaultValue || '')

  const activeTab = value ?? internalValue
  const setActiveTab = (newValue: string) => {
    if (!value) {
      setInternalValue(newValue)
    }
    onChange?.(newValue)
  }

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div className={cn('', className)} {...props}>
        {children}
      </div>
    </TabsContext.Provider>
  )
}

export interface TabListProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
  /** Visual variant */
  variant?: 'default' | 'pills' | 'underline'
  /** Full width tabs */
  fullWidth?: boolean
  /** Scrollable on mobile (default: true) */
  scrollable?: boolean
}

/**
 * Tab list container
 * Scrollable on mobile with larger touch targets
 */
export function TabList({
  children,
  variant = 'default',
  fullWidth = false,
  scrollable = true,
  className,
  ...props
}: TabListProps) {
  return (
    <div
      role="tablist"
      className={cn(
        'flex',
        variant === 'default' && 'gap-1 border-b border-border',
        variant === 'pills' && 'gap-2 p-1 bg-surface-elevated',
        variant === 'underline' && 'gap-4 border-b border-border',
        fullWidth && 'w-full',
        // Mobile: horizontal scroll with touch-friendly targets
        scrollable && 'overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export interface TabProps extends HTMLAttributes<HTMLButtonElement> {
  /** Tab value (must match TabPanel value) */
  value: string
  /** Tab icon */
  icon?: ReactNode
  /** Badge/count */
  badge?: ReactNode
  /** Disabled state */
  disabled?: boolean
  children: ReactNode
}

/**
 * Individual tab button
 * Larger touch targets on mobile for better usability
 */
export function Tab({
  value,
  icon,
  badge,
  disabled = false,
  children,
  className,
  ...props
}: TabProps) {
  const { activeTab, setActiveTab } = useTabsContext()
  const isActive = activeTab === value

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      aria-controls={`tabpanel-${value}`}
      id={`tab-${value}`}
      tabIndex={isActive ? 0 : -1}
      disabled={disabled}
      onClick={() => setActiveTab(value)}
      className={cn(
        'flex items-center gap-2 font-medium whitespace-nowrap',
        // Mobile: larger touch targets (44px minimum)
        'px-4 py-3 text-body sm:px-4 sm:py-2 sm:text-body-sm',
        'transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        // Default variant styling
        'border-b-2 -mb-px',
        isActive
          ? 'border-primary text-primary'
          : 'border-transparent text-text-muted hover:text-text-primary hover:border-border',
        // Touch feedback
        'active:bg-surface-elevated/50',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      {...props}
    >
      {icon && <span className="w-4 h-4">{icon}</span>}
      <span>{children}</span>
      {badge && <span>{badge}</span>}
    </button>
  )
}

export interface TabPanelsProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

/**
 * Tab panels container
 */
export function TabPanels({
  children,
  className,
  ...props
}: TabPanelsProps) {
  return (
    <div className={cn('mt-4', className)} {...props}>
      {children}
    </div>
  )
}

export interface TabPanelProps extends HTMLAttributes<HTMLDivElement> {
  /** Panel value (must match Tab value) */
  value: string
  children: ReactNode
}

/**
 * Individual tab panel
 */
export function TabPanel({
  value,
  children,
  className,
  ...props
}: TabPanelProps) {
  const { activeTab } = useTabsContext()
  const isActive = activeTab === value

  if (!isActive) return null

  return (
    <div
      role="tabpanel"
      id={`tabpanel-${value}`}
      aria-labelledby={`tab-${value}`}
      tabIndex={0}
      className={cn('animate-fade-in', className)}
      {...props}
    >
      {children}
    </div>
  )
}

/**
 * Pill-style tabs variant
 */
export interface PillTabsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Tab options */
  options: { value: string; label: string; icon?: ReactNode; badge?: ReactNode }[]
  /** Active value */
  value: string
  /** Change handler */
  onChange: (value: string) => void
  /** Size */
  size?: 'sm' | 'md'
}

export function PillTabs({
  options,
  value,
  onChange,
  size = 'md',
  className,
  ...props
}: PillTabsProps) {
  return (
    <div
      role="tablist"
      className={cn(
        'inline-flex p-1 bg-surface-elevated gap-1',
        // Mobile: horizontal scroll
        'overflow-x-auto scrollbar-hide',
        className
      )}
      {...props}
    >
      {options.map((option) => {
        const isActive = option.value === value

        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(option.value)}
            className={cn(
              'flex items-center gap-2 font-medium transition-all whitespace-nowrap',
              // Size variants with mobile-first approach
              size === 'sm' && 'px-3 py-2 text-body-sm sm:py-1.5',
              size === 'md' && 'px-4 py-2.5 text-body sm:py-2',
              isActive
                ? 'bg-primary text-text-inverse'
                : 'text-text-muted hover:text-text-primary active:bg-surface-overlay/50'
            )}
          >
            {option.icon && <span className="w-4 h-4">{option.icon}</span>}
            <span>{option.label}</span>
            {option.badge && <span>{option.badge}</span>}
          </button>
        )
      })}
    </div>
  )
}

export default Tabs
